/**
 * Deterministic plausibility guards for a custom ROI model.
 *
 * WHY THIS EXISTS. `evaluateExpression` substitutes every `a.<id>` and
 * `c.<key>` as a RAW number (compute.ts:33) — there is no unit system in the
 * formula language, so a proportion stored as `60` instead of `0.6` inflates
 * its stream by 100× and the projection persists as "computed". Nothing in
 * generation, the intake contract boundary, or the write path rejected that.
 *
 * WHAT MAKES THE HARD ERRORS DETERMINISTIC. The repo already has exactly one
 * storage convention for values known to be proportions:
 *   - `BASELINE_REGISTRY`: every `unit: "percent"` definition carries
 *     `hardBounds: { min: 0, max: 1 }` (baselines/registry.ts).
 *   - `EvidenceReference.value` is documented as "normalized to the
 *     assumption's storage unit (for example, 0.25 for 25%)" (types.ts:129),
 *     and `validate-assumptions.ts` already rejects a percent-declared
 *     assumption outside [0, 1].
 *
 * Opportunity-local ROI facts (`c.<key>`) are invented by the model and never
 * pass through the baseline normalizer. Unambiguous percent names and explicit
 * units are authoritative. The ambiguous `_rate` suffix requires positive
 * semantic evidence, and the classifier is checked against the baseline
 * catalog in unit tests so it never hard-rejects a known dimensional metric.
 *
 * DELIBERATELY LOOSER THAN THE PROFILE WRITE PATH. `inferDefinitionFallback`
 * (intake-chat/stages/normalize.ts:276) stamps `unit: "percent"` and
 * `hardBounds: { min: 0, max: 1 }` on ANY novel `_rate$` key, so a dimensional
 * `exchange_rate` arriving through the org profile is already clamped there.
 * These guards cannot borrow that rule, because they REFUSE A SAVE and the
 * `c.<key>` namespace is open-ended. A `_rate` name the qualifier list does not
 * recognize is therefore never rejected as an out-of-range proportion; unless
 * the name states a non-proportional basis of its own, the missing metadata is
 * reported as a warning so the gap stays visible.
 */

import { evaluateExpression, isPercentLike } from "./compute";
import type { ProposedAssumption, ProposedValueStream } from "./types";

/** Token grammar shared with the runtime evaluator and `roi-assumption-service`. */
const FORMULA_TOKEN = /\b([ac])\.(\w+)\b/g;

/**
 * Name shapes that denote a proportion. Matched as a whole snake_case segment
 * so infix uses are caught too (`missed_charge_rate_surgical` is a percent
 * baseline in the catalog).
 */
const PROPORTION_SEGMENT = /(?:_rate|_pct|_percent|_percentage)(?:_|$)/;
const UNAMBIGUOUS_PROPORTION_SEGMENT = /(?:_pct|_percent|_percentage)(?:_|$)/;

/**
 * Nouns that name a part-of-whole only when they END the key. Unlike `_rate`
 * and `_pct`, these must not match infix: `referral_capture_value` is a
 * dollar amount, `patient_share_count` a headcount — the trailing noun is what
 * the value actually is. `pa_labor_savings_capture` (unit "ratio") is the
 * shape this exists for, where nothing else marks it as a fraction.
 */
const TERMINAL_PROPORTION_SEGMENT = /_(?:capture|share|fraction|proportion)$/;

/**
 * Units that DECLARE a part-of-whole, whatever the name. Models routinely
 * write the unit as prose ("fraction of delay-driven cancellations avoided",
 * "share of freed physician time converted to realized cost savings") — a
 * clearer statement of 0-1 storage than any name shape, and one that exact
 * matching against the literal "percent" threw away.
 *
 * Bare "ratio" is deliberately NOT here: a ratio may legitimately exceed 1
 * (a 1.42 current-to-target ratio), and the repo already treats it as a plain
 * number. Only the explicit "<part> of <whole>" phrasings are conclusive.
 *
 * A trailing BASIS is conclusive for the same reason the whole-string alias
 * is: "percent/year", "percent per day", "% per 1,000 encounters" say percent
 * outright and then say what the percentage is measured over. The basis is a
 * denominator, not a competing storage form — 12 percent per year is the
 * fraction 0.12, exactly as bare "percent" would be — so reading it as a
 * dimension that rules the convention out is what let `annual_turnover_rate`
 * reach the ledger holding 12 under a source that says "12% annually". Note
 * the basis must follow the alias immediately: "percentage points per year"
 * is points, not a fraction, and stays out because "points" intervenes.
 *
 * Kept in step with `_PERCENT_UNIT_WITH_BASIS` in
 * `apps/ai-graph/src/ai_graph/contract/assumption_units.py`, which holds the
 * graph's writes to the 0-1 bound for the same spellings. A unit that is a
 * fraction to one side and a plain number to the other is the hundredfold
 * disagreement these guards exist to remove, so the two change together.
 */
const PROPORTION_UNIT_PROSE =
  /^(?:fraction|proportion|share|percent(?:age)?|%)(?:\s+of\b|\s*\/|\s+per\b|$)/;

/**
 * Positive evidence that an otherwise ambiguous `_rate` key is a proportion.
 * A negative qualifier list is unsafe because the namespace is open-ended:
 * dimensional values such as `exchange_rate` and `monthly_burn_rate` cannot
 * all be anticipated. `_pct`/`_percent`/`_percentage` remain unambiguous, and
 * an explicit unit remains authoritative in `isProportionFact` below.
 *
 * A name this list does not recognize is NOT silently trusted: an unrecognized
 * `_rate` fact whose value falls outside [0, 1] and which declares no unit
 * raises `ambiguous_rate_fact_missing_unit`, so the one piece of metadata that
 * would settle it is asked for instead of guessed at. The list must cover
 * every `unit: "percent"` key in `BASELINE_REGISTRY`; a unit test enforces it.
 *
 * The registry is its evidence base but not its only source: `rework` and
 * `amendment` were added for `pa_manual_rework_rate` and
 * `note_coding_query_amendment_rate`, both minted by the intake graph in live
 * runs holding a share. `query` was considered with them and deliberately left
 * out, because a query rate is as readily queries per encounter as a share of
 * notes queried, and this list takes only nouns that name a part of a whole
 * outright.
 *
 * Kept character-identical to `_PROPORTION_RATE_QUALIFIER` in
 * `apps/ai-graph/src/ai_graph/contract/assumption_units.py`, which applies the
 * same rule to the graph's own writes. A name that is a fraction on one side
 * and a dimensional number on the other is the hundredfold disagreement these
 * guards exist to remove, so the two lists change together.
 */
const PROPORTION_RATE_QUALIFIER =
  /(?:^|_)(?:abandonment|accuracy|adoption|amendment|approval|automation|backfill|burnout|cancellation|capture|collection|completion|compliance|denial|error|infection|leakage|missed_charge|mortality|no_show|readmission|recovery|reduction|resolution|review|rework|success|turnover|utilization)(?:_|$)/;

/**
 * Names that state their own non-proportional basis: a price (`hourly_rate`), a
 * multiplier (`agency_premium_rate`), a throughput (`patient_arrival_rate`).
 * This list is NOT evidence enough to classify — being open-ended, it was what
 * made unlisted dimensional names like `exchange_rate` hard-rejectable — so it
 * gates nothing. It only suppresses `ambiguous_rate_fact_missing_unit`, where
 * an omission costs one extra warning and never a refused save.
 */
const SELF_DESCRIBING_RATE_BASIS =
  /(?:^|_)(?:hourly|wage|wages|salary|salaries|pay|price|prices|dollar|dollars|usd|rates?_per|premium|multiplier|factor|index|multiple|arrival|admission|throughput)(?:_|$)/;

/** True when a declared unit states a part-of-whole outright. */
export function isProportionUnit(
  declaredUnit: string | null | undefined,
): boolean {
  const unit = declaredUnit?.trim().toLowerCase();
  if (!unit) return false;
  return isPercentLike(null, unit) || PROPORTION_UNIT_PROSE.test(unit);
}

/**
 * Units that name a part-of-whole WITHOUT stating which form it is in.
 *
 * A unit is free text a model wrote, so it falls into three kinds and not two:
 * it can state the percent convention (`percent`, `%`, `fraction of ...`), it
 * can state a dimension that rules the convention out (`claims/month`,
 * `minutes/visit`, `providers`), or it can state neither. These are the third
 * kind. `rate` is the model's habitual word for a percentage and equally its
 * word for a price (`hourly_rate`) or a throughput (`patient_arrival_rate`);
 * `ratio` is kept out of `PROPORTION_UNIT_PROSE` above because it may
 * legitimately exceed 1 — which makes it unable to PROVE a fraction, not able
 * to rule one out. Either way the unit is evidence that a value may be a part
 * of a whole and no evidence at all about which form it is in, so the value's
 * own name is the only signal left and must not be overruled by the unit.
 *
 * Narrower than it looks from the graph side: `_AMBIGUOUS_RATE_UNIT` in
 * `apps/ai-graph/src/ai_graph/contract/assumption_units.py` draws the same
 * three-way distinction but takes `rate` alone, because there the verdict
 * REFUSES a write and a `ratio` of 1.42 is a figure a writer may legitimately
 * mean. Here the verdict only decides which of two renderings a stored figure
 * gets, so the wider set costs nothing and keeps a `_capture` under "ratio"
 * reading as the fraction it is.
 */
const AMBIGUOUS_PROPORTION_UNIT = /^(?:rates?|ratio)$/;

/**
 * True when a declared unit settles the storage form on its own, either way.
 *
 * False for a blank unit, the placeholder `number`, and the ambiguous
 * part-of-whole words — the only cases where a name heuristic gets to speak.
 * Everything else, recognized as a proportion or not, has said enough that a
 * name must not overrule it.
 */
export function unitClassifiesStorageForm(
  declaredUnit: string | null | undefined,
): boolean {
  const unit = declaredUnit?.trim().toLowerCase();
  if (!unit || unit === "number") return false;
  return !AMBIGUOUS_PROPORTION_UNIT.test(unit);
}

/**
 * A declared money unit settles it: whatever the name suggests, the value is
 * an amount, not a fraction. Checked ahead of every name heuristic so a
 * `*_capture_*` or `*_share_*` currency assumption is never rescaled.
 */
function isCurrencyUnit(
  unit: string | null | undefined,
  format: string | null | undefined,
): boolean {
  const u = unit?.trim().toLowerCase() ?? "";
  const f = format?.trim().toLowerCase() ?? "";
  return (
    f === "currency" ||
    u === "currency" ||
    u === "$" ||
    u === "usd" ||
    u === "dollars" ||
    u.startsWith("$/")
  );
}

/** True when `key` names a value that must be stored as a 0–1 fraction. */
export function isProportionFactKey(key: string): boolean {
  const lower = key.toLowerCase();
  if (TERMINAL_PROPORTION_SEGMENT.test(lower)) return true;
  if (!PROPORTION_SEGMENT.test(lower)) return false;
  // Unambiguous percent markers win outright.
  if (UNAMBIGUOUS_PROPORTION_SEGMENT.test(lower)) return true;
  return PROPORTION_RATE_QUALIFIER.test(lower);
}

export function isProportionFact(
  key: string,
  declaredUnit: string | null | undefined,
): boolean {
  // A unit that classifies the storage form outranks the name — including an
  // unambiguous `_pct`/`_percent` name, which used to short-circuit above this
  // line. That exemption was written for BAD unit metadata, and under the
  // three-way reading of a unit "bad" means one that says nothing: blank,
  // `number`, a bare `rate`/`ratio`. A stated dimension is not bad metadata,
  // and letting the name beat it made `error_percentage_per_1000 = 4` in
  // "events/1,000 encounters" an out-of-range proportion here while the
  // assumptions page rendered the same fact as 4 events — which is the
  // cross-layer disagreement these guards exist to remove. `discount_pct` in
  // "number" is unaffected: `number` still hands the question to the name.
  return unitClassifiesStorageForm(declaredUnit)
    ? isProportionUnit(declaredUnit)
    : isProportionFactKey(key);
}

/**
 * True when an assumption holds a 0–1 fraction.
 *
 * `unit`/`format` are free text authored by the model, so the exact-string
 * `isPercentLike` check misses a proportion that describes itself in prose
 * ("share of freed physician time converted to realized cost savings"). Such
 * an assumption was invisible to formatting, to the editor's ÷100, and to the
 * range guard alike, so a reader who typed `50` for 50% got a silent 100×
 * projection. The `a.<id>` namespace obeys the same naming conventions as
 * `c.<key>`, so the name shape is a second authoritative signal — deliberately
 * the SAME classifier, so a name is never a proportion on one side of the
 * formula and dimensional on the other.
 */
export function isProportionAssumption(assumption: {
  id: string;
  unit?: string | null;
  format?: string | null;
}): boolean {
  if (isCurrencyUnit(assumption.unit, assumption.format)) return false;
  if (isPercentLike(assumption.format, assumption.unit)) return true;
  if (isProportionUnit(assumption.unit)) return true;
  // The name is the LAST signal, not one term of an OR. Read as an OR it beat
  // every dimensional unit, so `denial_rate` in "claims/month" was a fraction
  // here and a plain number on the assumptions page — the two answers this
  // function's own docstring promises can never differ. Same precedence, same
  // order, as `isPercentLike` in packages/ui and `isProportionFact` above.
  if (unitClassifiesStorageForm(assumption.unit)) return false;
  return isProportionFactKey(assumption.id);
}

export type PlausibilityIssueCode =
  | "proportion_fact_out_of_range"
  | "percent_assumption_out_of_range"
  | "proportion_fact_missing_unit"
  | "ambiguous_rate_fact_missing_unit"
  | "benefit_exceeds_unreduced_baseline";

export interface PlausibilityIssue {
  code: PlausibilityIssueCode;
  /** Definition path, e.g. `contextValues.overtime_reduction_rate`. */
  path: string;
  severity: "error" | "warning";
  message: string;
  /** Stable error identity; excludes reader-facing labels in `message`. */
  fingerprintValue?: string;
}

interface CollectPlausibilityIssuesArgs {
  /** ACTIVE streams only — a dismissed stream contributes nothing to totals. */
  valueStreams: ProposedValueStream[];
  assumptions: ProposedAssumption[];
  /** Resolved fact values: org profile + local overrides + `contextValues`. */
  context: Record<string, number>;
  /** Effective assumption values (`userResponses` over `default`). Assumptions
   *  not listed fall back to their own `default`. */
  assumptionValues?: Record<string, number>;
  /** `definition.contextMeta` — the only place a fact's unit is recorded. */
  contextMeta?: Record<string, { label: string; unit?: string | null }>;
}

function factLabel(
  key: string,
  contextMeta: CollectPlausibilityIssuesArgs["contextMeta"],
): string {
  const meta =
    contextMeta && Object.hasOwn(contextMeta, key)
      ? contextMeta[key]
      : undefined;
  return meta?.label ?? key.replace(/_/g, " ");
}

/** `0.6` for `60`, so the message can name the value the user meant. */
function asFraction(value: number): string {
  const fraction = value / 100;
  return Number.isInteger(fraction * 1000)
    ? String(fraction)
    : fraction.toPrecision(3);
}

interface StreamReferences {
  assumptionIds: string[];
  contextKeys: string[];
}

function referencesOf(formulaExpr: string): StreamReferences {
  const assumptionIds = new Set<string>();
  const contextKeys = new Set<string>();
  for (const match of formulaExpr.matchAll(FORMULA_TOKEN)) {
    const [, kind, name] = match;
    if (!kind || !name) continue;
    if (kind === "a") assumptionIds.add(name);
    else contextKeys.add(name);
  }
  return {
    assumptionIds: [...assumptionIds],
    contextKeys: [...contextKeys],
  };
}

function effectiveAssumptionValues(
  assumptions: ProposedAssumption[],
  overrides: Record<string, number> | undefined,
): Map<string, number> {
  const values = new Map<string, number>();
  for (const assumption of assumptions) {
    const override = overrides?.[assumption.id];
    const value =
      typeof override === "number" && Number.isFinite(override)
        ? override
        : assumption.default;
    if (value != null && Number.isFinite(value))
      values.set(assumption.id, value);
  }
  return values;
}

/**
 * A benefit stream's value with every proportion input forced to 1 — the
 * un-reduced spend the stream claims a share of. A stream can never exceed it:
 * `hours × reduction × cost × 12` is at most `hours × 1 × cost × 12`.
 *
 * Only claimed for a pure product of nonnegative literals and inputs. That
 * structure proves the expression is monotone non-decreasing in every
 * proportion input. More expressive formulas are reported as not analyzable:
 * a local numeric probe cannot prove monotonicity through the whole interval.
 */
export interface BenefitStreamPlausibility {
  streamId: string;
  value: number;
  /** Null when the stream references no proportion input, or is not analyzable. */
  unreducedBaseline: number | null;
  analyzable: boolean;
  exceedsBaseline: boolean;
}

const PRODUCT_FACTOR = /^(?:\d+(?:\.\d*)?|\.\d+|[ac]\.\w+)$/;

/**
 * Return true only for the small formula subset whose monotonicity is evident
 * from its structure: multiplication of nonnegative constants and inputs.
 * Parentheses around individual factors do not change that structure.
 */
function isProvablyNonnegativeProduct(
  formulaExpr: string,
  assumptions: Record<string, number>,
  context: Record<string, number>,
): boolean {
  const normalized = formulaExpr.replace(/[\s()]/g, "");
  // Only whitespace and parentheses are stripped, so any `+`, `-` or `/` in the
  // expression survives into a factor and fails `PRODUCT_FACTOR`. Nothing but a
  // genuine product can reach the loop below.
  const factors = normalized.split("*");
  if (factors.some((factor) => !PRODUCT_FACTOR.test(factor))) return false;

  for (const factor of factors) {
    const reference = factor.match(/^([ac])\.(\w+)$/);
    const value = reference
      ? reference[1] === "a"
        ? assumptions[reference[2] ?? ""]
        : context[reference[2] ?? ""]
      : Number(factor);
    if (value == null || !Number.isFinite(value) || value < 0) return false;
  }
  return true;
}

export function assessBenefitStreamPlausibility(args: {
  stream: ProposedValueStream;
  assumptions: ProposedAssumption[];
  context: Record<string, number>;
  assumptionValues?: Record<string, number>;
  contextMeta?: CollectPlausibilityIssuesArgs["contextMeta"];
}): BenefitStreamPlausibility {
  const { stream, context } = args;
  const assumptionMap = Object.fromEntries(
    effectiveAssumptionValues(args.assumptions, args.assumptionValues),
  );
  const value = evaluateExpression(stream.formulaExpr, assumptionMap, context);
  const refs = referencesOf(stream.formulaExpr);

  const percentByUnit = new Set(
    args.assumptions
      .filter(isProportionAssumption)
      .map((assumption) => assumption.id),
  );
  const proportionContextKeys = refs.contextKeys.filter((key) =>
    isProportionFact(key, args.contextMeta?.[key]?.unit),
  );
  const proportionAssumptionIds = refs.assumptionIds.filter((id) =>
    percentByUnit.has(id),
  );

  const base: BenefitStreamPlausibility = {
    streamId: stream.id,
    value,
    unreducedBaseline: null,
    analyzable: false,
    exceedsBaseline: false,
  };
  if (proportionContextKeys.length + proportionAssumptionIds.length === 0) {
    return base;
  }

  if (!isProvablyNonnegativeProduct(stream.formulaExpr, assumptionMap, context))
    return base;

  const probe = (
    contextOverride: Record<string, number>,
    assumptionOverride: Record<string, number>,
  ): number =>
    evaluateExpression(
      stream.formulaExpr,
      { ...assumptionMap, ...assumptionOverride },
      { ...context, ...contextOverride },
    );

  const unreducedBaseline = probe(
    Object.fromEntries(proportionContextKeys.map((key) => [key, 1])),
    Object.fromEntries(proportionAssumptionIds.map((id) => [id, 1])),
  );
  if (!Number.isFinite(unreducedBaseline) || unreducedBaseline <= 0) {
    return base;
  }
  return {
    streamId: stream.id,
    value,
    unreducedBaseline,
    analyzable: true,
    // Float slack: a stream whose proportions are all exactly 1 is equal, not
    // over — that is a 100%-of-baseline model, which is implausible but not
    // arithmetically impossible, so only a strict excess is reported.
    exceedsBaseline: value > unreducedBaseline * (1 + 1e-9),
  };
}

const currency = (value: number): string =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(value);

/**
 * Every deterministic plausibility finding for a model. Errors are
 * arithmetically impossible under the repo's storage conventions; warnings are
 * missing metadata that made an error possible.
 */
export function collectModelPlausibilityIssues(
  args: CollectPlausibilityIssuesArgs,
): PlausibilityIssue[] {
  const { valueStreams, assumptions, context, contextMeta } = args;
  const issues: PlausibilityIssue[] = [];

  // Only inputs a live formula references matter. A vestigial contextKey left
  // behind by an edited model changes no number and must not block a save.
  const referencedContextKeys = new Set<string>();
  const referencedAssumptionIds = new Set<string>();
  for (const stream of valueStreams) {
    const refs = referencesOf(stream.formulaExpr);
    for (const key of refs.contextKeys) referencedContextKeys.add(key);
    for (const id of refs.assumptionIds) referencedAssumptionIds.add(id);
  }

  for (const key of [...referencedContextKeys].sort()) {
    const declaredUnit = contextMeta?.[key]?.unit ?? null;
    const unitless =
      !declaredUnit?.trim() || declaredUnit.trim().toLowerCase() === "number";
    const proportion = isProportionFact(key, declaredUnit);
    const label = factLabel(key, contextMeta);
    const value = Object.hasOwn(context, key) ? context[key] : undefined;

    if (!proportion) {
      // A `_rate` name the qualifier list does not recognize. It may still be a
      // proportion typed as a whole percentage — the very bug these guards
      // exist for — but the name alone cannot prove that, and an error would
      // refuse legitimate dimensional facts such as `monthly_burn_rate`. Ask
      // for the metadata that settles it rather than guessing either way.
      if (
        unitless &&
        PROPORTION_SEGMENT.test(key.toLowerCase()) &&
        !SELF_DESCRIBING_RATE_BASIS.test(key.toLowerCase()) &&
        value != null &&
        Number.isFinite(value) &&
        (value < 0 || value > 1)
      ) {
        issues.push({
          code: "ambiguous_rate_fact_missing_unit",
          path: `contextMeta.${key}`,
          severity: "warning",
          message: `Fact "${key}" ("${label}") is ${value} and declares no unit. A "_rate" name can denote a proportion — in which case this should be ${asFraction(value)} — or a dimensional value that legitimately exceeds 1. Declare a unit so the scale can be checked.`,
        });
      }
      continue;
    }

    if (isProportionFactKey(key) && unitless) {
      issues.push({
        code: "proportion_fact_missing_unit",
        path: `contextMeta.${key}`,
        severity: "warning",
        message: `Fact "${key}" ("${label}") is a proportion but declares no unit, so nothing tells a reader — or the person typing the value — whether it is a fraction or a whole percentage.`,
      });
    }

    if (value == null || !Number.isFinite(value)) continue;
    if (value >= 0 && value <= 1) continue;
    issues.push({
      code: "proportion_fact_out_of_range",
      path: `contextValues.${key}`,
      severity: "error",
      message: `Fact "${key}" ("${label}") is ${value}, outside the 0–1 range every proportion in this model is stored in. Formulas multiply it in raw, so ${value} scales the stream by ${value}× instead of ${asFraction(value)}×. Enter ${asFraction(value)} for ${value}%.`,
      fingerprintValue: String(value),
    });
  }

  for (const assumption of assumptions) {
    if (!referencedAssumptionIds.has(assumption.id)) continue;
    if (!isProportionAssumption(assumption)) continue;
    const override = args.assumptionValues?.[assumption.id];
    const value =
      typeof override === "number" && Number.isFinite(override)
        ? override
        : assumption.default;
    if (value == null || !Number.isFinite(value)) continue;
    if (value >= 0 && value <= 1) continue;
    // Say which signal classified it: an explicit unit is quotable, a prose
    // unit is not, and pointing at the name is what makes the verdict
    // arguable rather than mysterious.
    const basis = isPercentLike(assumption.format, assumption.unit)
      ? `declares unit "${assumption.unit}"/format "${assumption.format}"`
      : `is named as a proportion`;
    issues.push({
      code: "percent_assumption_out_of_range",
      path: `assumptions.${assumption.id}`,
      severity: "error",
      message: `Assumption "${assumption.id}" ("${assumption.name}") ${basis} but its value is ${value}. Proportion assumptions are stored as decimals — use ${asFraction(value)} for ${value}%.`,
      fingerprintValue: String(value),
    });
  }

  for (const stream of valueStreams) {
    if (stream.direction !== "benefit") continue;
    const assessment = assessBenefitStreamPlausibility({
      stream,
      assumptions,
      context,
      ...(args.assumptionValues
        ? { assumptionValues: args.assumptionValues }
        : {}),
      ...(contextMeta ? { contextMeta } : {}),
    });
    if (!assessment.analyzable || !assessment.exceedsBaseline) continue;
    const baseline = assessment.unreducedBaseline ?? 0;
    issues.push({
      code: "benefit_exceeds_unreduced_baseline",
      path: `valueStreams.${stream.id}`,
      severity: "error",
      message: `Benefit stream "${stream.id}" is ${currency(assessment.value)}/yr, more than the ${currency(baseline)}/yr it derives from (the same formula with every proportion at 100%). A stream cannot save more than the spend it reduces.`,
      fingerprintValue: `${assessment.value}:${baseline}`,
    });
  }

  return issues;
}
